import { ReactNode } from 'react';
import PrimaryButton from '../PrimaryButton';
import { Icon } from '@iconify/react';
import closeIcon from '@iconify/icons-material-symbols/close-rounded';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../Dialog';

interface Props {
  cancelText: string | null;
  confirmText: string | null;
  handleHide: () => void;
  handlePerformAction: () => void | Promise<void>;
  show: boolean;
  subtitle: string | ReactNode;
  title: string | ReactNode;
}

const ConfirmationModal = ({
  cancelText,
  confirmText,
  handleHide,
  handlePerformAction,
  show,
  subtitle,
  title,
}: Props) => {
  const handleConfirm = async () => {
    try {
      await handlePerformAction();
    } catch {
      console.error('Could not complete action');
    } finally {
      handleHide();
    }
  };

  return (
    <Dialog
      open={show}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleHide();
        }
      }}
    >
      <DialogContent className='w-[min(92vw,700px)] rounded-[1.75rem] border-0 px-[1.6rem] pb-[2.4rem] pt-[4.8rem] shadow-[0_16px_48px_rgba(15,23,42,0.22)] min-[480px]:px-[2.4rem] min-[480px]:pb-[3.2rem] min-[480px]:pt-[5.6rem] sm:px-[6.1rem] sm:pb-[6.2rem] sm:pt-[6.2rem]'>
        <button
          type='button'
          className='absolute right-[1.25rem] top-[1.25rem] inline-flex h-[3rem] w-[3rem] cursor-pointer items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 min-[480px]:right-[2rem] min-[480px]:top-[2rem]'
          onClick={handleHide}
          aria-label='Close confirmation dialog'
        >
          <Icon className='h-[2.4rem] w-[2.4rem]' icon={closeIcon} />
        </button>

        <DialogHeader className='items-center text-center'>
          <DialogTitle asChild>
            <p className="font-['GT-Walsheim-Pro-Regular'] text-[2.2rem] leading-[1.3] text-slate-900">{title}</p>
          </DialogTitle>
        </DialogHeader>

        <DialogBody className='mt-2 text-center text-[1.8rem] leading-[1.5] text-slate-700'>{subtitle}</DialogBody>

        <DialogFooter className='mt-[2rem] flex flex-col items-center justify-center gap-[1rem] min-[480px]:gap-[1.5rem] sm:mt-[5rem] sm:flex-row sm:gap-[2rem]'>
          {cancelText && (
            <div className='w-[80%] sm:max-w-[18rem]'>
              <PrimaryButton small alertSecondary onClick={handleHide}>
                {cancelText}
              </PrimaryButton>
            </div>
          )}
          {confirmText && (
            <div className='w-[80%] sm:max-w-[18rem]'>
              <PrimaryButton small onClick={handleConfirm}>
                {confirmText}
              </PrimaryButton>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationModal;
